import React, { Fragment } from "react";

import { ArrowRight as ArrowRightIcon } from "react-feather";
import {
  Box,
  IconButton,
  Typography,
  colors,
  makeStyles,
} from "@material-ui/core";
import { Height as HeightIcon } from "@material-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import {
  differenceInMilliseconds,
  format,
  formatDistanceStrict,
} from "date-fns";
import { get } from "lodash";

import { formatPostcode } from "@ds-developer/frontend";
import Label from "@ds-developer/label";
import MapLink from "@ds-developer/map-link";

import { TodaysLessonProps } from "./types";

import { useTealium } from "@hubcapp/context";

const useStyles = makeStyles(theme => ({
  lesson: {
    padding: theme.spacing(2, 1, 2, 2),
    justifyContent: "space-between",
    boxShadow: "inset 0px -1px 0px #EEEEEE",
  },
  startTime: {
    fontWeight: theme.typography.fontWeightBold,
  },
  endTime: {
    color: colors.blueGrey[600],
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.5),
    [theme.breakpoints.between("sm", "lg")]: {
      marginTop: 0,
    },
  },
  postcode: {
    color: colors.blueGrey[600],
    marginRight: theme.spacing(6),
    [theme.breakpoints.only("sm")]: {
      marginRight: theme.spacing(2),
    },
  },
  lessonDate: {
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
    marginRight: "0.5rem",
    width: "80px",
    minWidth: "80px",

    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      alignItems: "center",
      width: "170px",
    },

    [theme.breakpoints.up("xl")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      width: "120px",
    },
  },
  content: {
    position: "relative",
    flex: 1,
    [theme.breakpoints.between("sm", "lg")]: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
  },
  name: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
    overflow: "hidden",
    maxWidth: "calc(100% - 70px)",
    textOverflow: "ellipsis",

    [theme.breakpoints.between("sm", "lg")]: {
      maxWidth: "100%",
      marginRight: theme.spacing(2),
    },
  },
  lessonPickupAddress: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: "170px",
    marginTop: theme.spacing(0.5),

    [theme.breakpoints.up("sm")]: {
      marginTop: 0,
    },

    [theme.breakpoints.up("xl")]: {
      marginTop: theme.spacing(0.5),
    },
  },

  lessonStatus: {
    position: "absolute",
    height: "100%",
    display: "flex",
    alignItems: "center",
    top: 0,
    right: 0,

    [theme.breakpoints.between("sm", "lg")]: {
      position: "relative",
    },
  },
  lessonLink: {
    width: "50px",
    height: "50px",
    "& svg": {
      color: colors.blueGrey[900],
    },
  },
  break: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FAFAFA",
    boxShadow: "inset 0px -1px 0px #EEEEEE",
    color: colors.blueGrey[600],
    lineHeight: 1,
    padding: theme.spacing(3, 0),

    "& svg": {
      fill: colors.blueGrey[600],
      marginRight: theme.spacing(0.5),
    },
  },
}));

const TodaysLesson = ({ lesson, nextLesson }: TodaysLessonProps) => {
  const classes = useStyles();
  const { trackEvent } = useTealium();
  const breakAfterLesson = nextLesson
    ? differenceInMilliseconds(
        new Date(get(nextLesson, "startDateTime")),
        new Date(get(lesson, "endDateTime")),
      )
    : 0;

  const showBreak = nextLesson && breakAfterLesson > 0;

  const trackOpenMapLink = () => {
    trackEvent({
      eventCategory: "Menu",
      eventAction: "Interaction",
      eventLabel: "Map",
    });
  };

  return (
    <Fragment>
      <Box
        data-test={`calendar-summary-lesson-${get(lesson, "startDateTime")}`}
        display="flex"
        className={classes.lesson}
      >
        <div className={classes.lessonDate}>
          <Typography
            variant="body2"
            data-test="calendar-summary-lesson-start-time"
            className={classes.startTime}
          >
            {format(new Date(get(lesson, "startDateTime")), "h:mm aaaa")}
          </Typography>
          <Typography
            className={classes.endTime}
            data-test="calendar-summary-lesson-end-time"
          >
            — {format(new Date(get(lesson, "endDateTime")), "h:mm aaaa")}
          </Typography>
        </div>

        <div className={classes.content}>
          <Typography
            data-test="calendar-summary-lesson-name"
            variant="body2"
            className={classes.name}
          >
            {get(lesson, "learnerName")}
          </Typography>
          <div className={classes.lessonPickupAddress}>
            <Typography
              className={classes.postcode}
              variant="body2"
              data-test="calendar-summary-lesson-postcode"
            >
              {get(lesson, "pickupPostcode")}
            </Typography>
            <MapLink
              postcode={
                formatPostcode(get(lesson, "pickupPostcode") || "") as string
              }
              onClick={trackOpenMapLink}
            />
          </div>

          {lesson.newLearner ? (
            <div
              className={classes.lessonStatus}
              data-test="calendar-summary-lesson-new-notice"
            >
              <Label color="success">New</Label>
            </div>
          ) : (
            <div className={classes.lessonStatus} />
          )}
        </div>

        <IconButton
          className={classes.lessonLink}
          component={RouterLink}
          to={`/learners/${get(lesson, "learnerId")}`}
          onClick={() => {
            trackEvent({
              eventCategory: "Homepage",
              eventAction: "Interaction",
              eventLabel: "View Today's Lesson",
            });
          }}
        >
          <ArrowRightIcon />
        </IconButton>
      </Box>

      {showBreak && (
        <Box
          flexGrow={1}
          className={classes.break}
          data-test="calendar-summary-lesson-break"
        >
          <HeightIcon />
          {`${formatDistanceStrict(
            new Date(get(nextLesson || {}, "startDateTime")),
            new Date(get(lesson || {}, "endDateTime")),
          )} break`}
        </Box>
      )}
    </Fragment>
  );
};

export default TodaysLesson;
